import {
  type Theme,
  Flex,
  Text,
  useTheme,
  Skeleton,
  Tooltip,
  InfoOutlinedIcon,
  type SxPropsWithTheme,
  mergeSx,
} from '@shared/components';
import { type ReactNode } from 'react';

export interface SummaryStatsCardProps {
  title: string;
  displayValue: string;
  additionalInfo?: string;
  loading?: boolean;
  color?: 'success' | 'error' | 'info' | 'warning' | 'deepPurple';
  tooltipText?: ReactNode;
  sx?: SxPropsWithTheme;
}

const getCardColors = (
  theme: Theme,
  color: 'success' | 'error' | 'info' | 'warning' | 'deepPurple' | 'default' = 'default',
) => {
  const colorMapping = {
    error: {
      backgroundColor: theme.palette.error.lighter,
      skeletonColor: theme.palette.error.light,
      titleAndInfoColor: theme.palette.error.dark,
      displayColor: theme.palette.error.dark,
    },
    success: {
      backgroundColor: theme.palette.success.lighter,
      skeletonColor: theme.palette.success.light,
      titleAndInfoColor: theme.palette.success.dark,
      displayColor: theme.palette.success.dark,
    },
    info: {
      backgroundColor: theme.palette.info.lighter,
      skeletonColor: theme.palette.info.light,
      titleAndInfoColor: theme.palette.info.dark,
      displayColor: theme.palette.info.dark,
    },
    warning: {
      backgroundColor: theme.palette.warning.lighter,
      skeletonColor: theme.palette.warning.light,
      titleAndInfoColor: theme.palette.warning.dark,
      displayColor: theme.palette.warning.dark,
    },
    deepPurple: {
      backgroundColor: theme.palette.deepPurple.lighter,
      skeletonColor: theme.palette.deepPurple.light,
      titleAndInfoColor: theme.palette.deepPurple.main,
      displayColor: theme.palette.deepPurple.main,
    },
    default: {
      backgroundColor: theme.palette.grey[100],
      skeletonColor: theme.palette.grey[200],
      titleAndInfoColor: theme.palette.text.secondary,
      displayColor: theme.palette.text.primary,
    },
  };

  return colorMapping[color] ?? colorMapping.default;
};

export default function SummaryStatsCard(props: Readonly<SummaryStatsCardProps>) {
  const { title, displayValue, additionalInfo, color, loading: isLoading, tooltipText, sx } = props;
  const theme = useTheme();

  const { backgroundColor, titleAndInfoColor, displayColor, skeletonColor } = getCardColors(
    theme,
    color,
  );

  return (
    <Flex
      sx={mergeSx(
        (theme) => ({
          backgroundColor,
          p: theme.spacing(4),
          borderRadius: theme.shape.rounded(2),
          gap: theme.spacing(4),
          flex: 1,
        }),
        sx,
      )}
    >
      <Flex sx={{ flexDirection: 'column', flex: 1 }}>
        <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(1) })}>
          <Text sx={{ color: titleAndInfoColor }}>{title}</Text>
          {tooltipText ? (
            <span aria-label='ข้อมูลเพิ่มเติม' style={{ display: 'inline-flex' }}>
              <Tooltip
                title={
                  typeof tooltipText === 'string' ? (
                    <Text variant='caption' sx={{ whiteSpace: 'pre-line' }}>
                      {tooltipText}
                    </Text>
                  ) : (
                    tooltipText
                  )
                }
                placement='top'
              >
                <InfoOutlinedIcon sx={{ color: titleAndInfoColor, fontSize: 24 }} />
              </Tooltip>
            </span>
          ) : null}
        </Flex>
        <Flex sx={(theme) => ({ alignItems: 'baseline', gap: theme.spacing(1) })}>
          <Text
            variant='h3'
            sx={{
              color: displayColor,
              width: '100%',
            }}
          >
            {isLoading ? <Skeleton sx={{ backgroundColor: skeletonColor }} /> : displayValue}
          </Text>
          <Text
            variant='caption'
            sx={{
              color: titleAndInfoColor,
            }}
          >
            {additionalInfo}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
